import React, { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../utils/apiConfig';

/**
 * Widget affichant le suivi du forfait jours
 * Pour les utilisateurs cadres (is_manager = true)
 */
const ForfaitJoursWidget = ({ token, compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const loadData = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/leave-balances/days`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();

      if (result.success) {
        setData(result.data);
      } else {
        setError(result.message || 'Erreur chargement');
      }
    } catch (err) {
      setError('Erreur connexion serveur');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/leave-balances/days/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const result = await response.json();

      if (result.success) {
        loadData();
      }
    } catch (err) {
      console.error('Erreur sync:', err);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <div style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>
          Chargement...
        </div>
      </div>
    );
  }

  // Non-cadre : ne rien afficher
  if (!data) {
    return null;
  }

  if (error && !data) {
    return (
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <div style={{ textAlign: 'center', color: '#dc3545', fontSize: '14px' }}>
          {error}
        </div>
      </div>
    );
  }

  const daysWorked = parseFloat(data.days_worked || 0);
  const forfaitDays = parseFloat(data.forfait_days || 218);
  const daysRemaining = parseFloat(data.days_remaining ?? (forfaitDays - daysWorked));
  const progress = forfaitDays > 0 ? (daysWorked / forfaitDays) * 100 : 0;
  const compensatoryDays = parseFloat(data.compensatory_days || 0);
  const compensatoryTaken = parseFloat(data.compensatory_days_taken || 0);
  const compensatoryRemaining = parseFloat(data.compensatory_days_remaining ?? (compensatoryDays - compensatoryTaken));
  const cpTaken = parseFloat(data.cp_taken || 0);
  const rttTaken = parseFloat(data.rtt_taken || 0);
  const sickDays = parseFloat(data.sick_days || 0);
  const rttDays = parseFloat(data.rtt_days || 0);

  // Calcul theorique a date (proportionnel)
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const endOfYear = new Date(now.getFullYear(), 11, 31);
  const yearProgress = (now - startOfYear) / (endOfYear - startOfYear);
  const theoreticalDays = forfaitDays * yearProgress;
  const delta = daysWorked - theoreticalDays;

  const getProgressColor = () => {
    if (delta >= 15) return '#dc3545';
    if (delta >= 5) return '#fd7e14';
    if (delta <= -15) return '#dc3545';
    if (delta <= -5) return '#ffc107';
    return '#28a745';
  };

  // Version compacte
  if (compact) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        background: '#f8f9fa',
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '13px'
      }}>
        <span>J</span>
        <strong>{daysWorked.toFixed(0)}j</strong>
        <span style={{ color: '#666' }}>/ {forfaitDays}j</span>
        <span style={{
          color: getProgressColor(),
          fontWeight: '600'
        }}>
          ({delta > 0 ? '+' : ''}{delta.toFixed(0)}j)
        </span>
      </div>
    );
  }

  const hasAbsences = cpTaken > 0 || rttTaken > 0 || sickDays > 0;
  const hasCompensatory = compensatoryDays > 0;

  // Version complete
  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          background: expanded ? '#f8f9fa' : 'white',
          transition: 'background 0.2s'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '22px' }}>Calendrier</span>
          <span style={{ fontWeight: '600', fontSize: '16px', color: '#333' }}>
            Mes jours
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{
              fontWeight: '700',
              fontSize: '16px',
              color: '#333'
            }}>
              {daysWorked.toFixed(0)}j
            </span>
            <span style={{ color: '#666', fontSize: '13px' }}>
              / {forfaitDays}j
            </span>
            <span style={{
              background: getProgressColor(),
              color: 'white',
              padding: '3px 8px',
              borderRadius: '10px',
              fontSize: '11px',
              fontWeight: '600'
            }}>
              {progress.toFixed(0)}%
            </span>
          </div>
          <span style={{
            fontSize: '12px',
            color: '#666',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.2s'
          }}>
            ▼
          </span>
        </div>
      </div>

      {/* Details (expandable) */}
      {expanded && (
        <div style={{ padding: '0 20px 20px' }}>
          {/* Progress bar */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{
              background: '#e9ecef',
              borderRadius: '10px',
              height: '20px',
              overflow: 'hidden',
              position: 'relative'
            }}>
              {/* Barre de progression reelle */}
              <div style={{
                background: getProgressColor(),
                height: '100%',
                width: `${Math.min(100, progress)}%`,
                transition: 'width 0.3s ease',
                borderRadius: '10px'
              }} />
              {/* Marqueur theorique */}
              <div style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${Math.min(100, (theoreticalDays / forfaitDays) * 100)}%`,
                width: '3px',
                background: '#333',
                opacity: 0.5
              }} />
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '6px',
              fontSize: '11px',
              color: '#666'
            }}>
              <span>0j</span>
              <span style={{ color: getProgressColor(), fontWeight: '600' }}>
                {delta > 0 ? `+${delta.toFixed(0)}j d'avance` : delta < 0 ? `${delta.toFixed(0)}j de retard` : 'A jour'}
              </span>
              <span>{forfaitDays}j</span>
            </div>
          </div>

          {/* Details cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            <div style={{
              background: '#f8f9fa',
              padding: '12px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>
                Jours travailles
              </div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#333' }}>
                {daysWorked.toFixed(0)}j
              </div>
            </div>

            <div style={{
              background: '#f8f9fa',
              padding: '12px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>
                Jours restants
              </div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#333' }}>
                {daysRemaining.toFixed(0)}j
              </div>
            </div>

            <div style={{
              background: '#f8f9fa',
              padding: '12px',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>
                Theorique a date
              </div>
              <div style={{ fontSize: '20px', fontWeight: '700', color: '#666' }}>
                {theoreticalDays.toFixed(0)}j
              </div>
            </div>
          </div>

          {/* Jours compensateurs */}
          {hasCompensatory && (
            <div style={{
              marginTop: '12px',
              padding: '10px 12px',
              background: '#ede7f6',
              borderRadius: '8px',
              fontSize: '12px',
              display: 'flex',
              gap: '16px',
              justifyContent: 'center',
              color: '#4a148c'
            }}>
              <span style={{ fontWeight: '600' }}>Jours compensateurs</span>
              <span>Acquis: {compensatoryDays.toFixed(0)}</span>
              <span>|</span>
              <span>Pris: {compensatoryTaken.toFixed(0)}</span>
              <span>|</span>
              <span style={{ fontWeight: '600' }}>Solde: {compensatoryRemaining.toFixed(0)}</span>
            </div>
          )}

          {/* Absences */}
          {hasAbsences && (
            <div style={{
              marginTop: '8px',
              padding: '10px 12px',
              background: '#fff3cd',
              borderRadius: '8px',
              fontSize: '12px',
              display: 'flex',
              gap: '16px',
              justifyContent: 'center'
            }}>
              {cpTaken > 0 && (
                <span>CP: {cpTaken.toFixed(0)}j</span>
              )}
              {rttTaken > 0 && (
                <span>RTT: {rttTaken.toFixed(0)}j{rttDays > 0 ? ` / ${rttDays.toFixed(0)}j` : ''}</span>
              )}
              {sickDays > 0 && (
                <span>Maladie: {sickDays.toFixed(0)}j</span>
              )}
            </div>
          )}

          {/* Sync button */}
          <button
            onClick={handleSync}
            disabled={syncing}
            style={{
              marginTop: '12px',
              width: '100%',
              padding: '8px',
              background: syncing ? '#6c757d' : '#e9ecef',
              color: '#495057',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              cursor: syncing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <span>{syncing ? '...' : '\u{1F504}'}</span>
            {syncing ? 'Synchronisation...' : 'Actualiser depuis les feuilles'}
          </button>

          {/* Derniere sync */}
          {data.last_sync_at && (
            <div style={{
              marginTop: '6px',
              fontSize: '10px',
              color: '#999',
              textAlign: 'center'
            }}>
              Derniere sync: {new Date(data.last_sync_at).toLocaleString('fr-FR')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ForfaitJoursWidget;
